import { ChangeEvent, useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { updateProject } from '../store';
import { setProjectNdaSignatures, addUser } from '../services/dataService';
import { Project, ProjectNdaSignature, User, UserRole } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import UserContactLinks from '../components/UserContactLinks';
import { 
  ArrowLeft, 
  Save, 
  Users, 
  Shield, 
  Settings as SettingsIcon,
  UserPlus,
  X,
  Crown,
  AlertCircle,
  FileSignature,
  RotateCcw,
  ImagePlus,
  Link2,
  ExternalLink,
  DollarSign,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { useApp } from '../providers/AppProvider';
import {
  getActiveProjectNdaSignature,
  getEffectiveProjectNdaSettings,
  getStandardProjectNdaClauses,
  revokeProjectNdaSignature,
  EXEMPTABLE_ROLES,
} from '../utils/projectNda';
import { canManageProjectSettings, getProjectMemberRole } from '../utils/projectAccess';
import { getDefaultCostTemplates, applyCostTemplate } from '../utils/costTemplates';
import { getUserProfilePath } from '../utils/userProfile';
import { APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import {
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
  WIDGET_STATUS_BADGE_INFO_CLASS,
  WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  WIDGET_STATUS_BADGE_WARNING_CLASS,
} from '../constants/widgetTheme';

export default function ProjectSettings() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { state, loading, reload } = useApp();
  const { projects, users } = useOrganizationData(state) as {
    projects: Project[];
    users: User[];
  };
  const project = useMemo(
    () => projects.find(p => String(p.id).toLowerCase() === String(projectId).toLowerCase()) ?? null,
    [projects, projectId]
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<{ userId: string; role: UserRole }[]>([]);
  const [ndaExemptStakeholders, setNdaExemptStakeholders] = useState(false);
  const [ndaAdditionalClausesText, setNdaAdditionalClausesText] = useState('');
  const [accountingModules, setAccountingModules] = useState<{ sr: boolean; fcm: boolean }>({
    sr: false,
    fcm: false,
  });
  const [appliedTemplate, setAppliedTemplate] = useState<string>('');
  const descriptionImageInputRef = useRef<HTMLInputElement>(null);

  // Create new user form state
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', role: 'developer' as UserRole });
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const costTemplates = useMemo(() => getDefaultCostTemplates(), []);

  const descriptionImageUrls = useMemo(() => {
    const matches = Array.from(description.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g));
    return matches.map((match) => match[1]).filter(Boolean);
  }, [description]);

  useEffect(() => {
    if (project) {
      const ndaSettings = getEffectiveProjectNdaSettings(project);
      setName(project.name);
      setDescription(project.description || '');
      setSelectedLeads(project.projectLeads || []);
      setSelectedMembers(project.teamMembers || []);
      setNdaExemptStakeholders(
        (ndaSettings.exemptRoles || []).includes('stakeholder')
      );
      setNdaAdditionalClausesText((ndaSettings.additionalClauses || []).join('\n'));
      setAccountingModules({
        sr: !!project.accountingModules?.sr,
        fcm: !!project.accountingModules?.fcm,
      });
    }
  }, [project]);

  const ndaStatusRows = useMemo(() => {
    if (!project) {
      return [] as Array<{
        user?: User;
        signature?: ProjectNdaSignature;
        label: string;
        email: string;
      }>;
    }

    type Row = { user?: User; signature?: ProjectNdaSignature; label: string; email: string };
    const map = new Map<string, Row>();

    const resolveUser = (userId: string) => {
      const normalizedMemberId = String(userId || '').trim().toLowerCase();
      return users.find((user) => {
        const normalizedUserId = String(user.id || '').trim().toLowerCase();
        const normalizedUserEmail = String(user.email || '').trim().toLowerCase();
        return normalizedUserId === normalizedMemberId || normalizedUserEmail === normalizedMemberId;
      });
    };

    const register = (userId: string) => {
      const user = resolveUser(userId);
      if (!user) return;
      const key = String(user.email || user.id).trim().toLowerCase();
      if (!key) return;
      const sig = getActiveProjectNdaSignature(project, user) || undefined;
      map.set(key, { user, signature: sig, label: user.name, email: user.email });
    };

    selectedLeads.forEach(register);
    selectedMembers.forEach((member) => register(member.userId));

    (project.ndaSignatures || []).forEach((sig) => {
      const key = String(sig.userEmail || sig.userId).trim().toLowerCase();
      const user = resolveUser(sig.userEmail) || resolveUser(sig.userId);
      const existing = map.get(key);
      const activeSig = user
        ? getActiveProjectNdaSignature(project, user)
        : !sig.revokedAt
          ? sig
          : undefined;

      map.set(key, {
        user: user || existing?.user,
        signature: activeSig || existing?.signature,
        label: user?.name || sig.userName || existing?.label || sig.userEmail,
        email: user?.email || sig.userEmail || existing?.email || '',
      });
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [project, selectedLeads, selectedMembers, users]);

  if (loading || !state) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading project settings...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!state.currentUser) return null;
  const currentUser = state.currentUser;
  const projectMemberRole = getProjectMemberRole(project, currentUser.id, currentUser.email);
  const canEdit = canManageProjectSettings(currentUser, project);
  // NDA exemption can only be changed by project owners/leads or global admins
  const canManageNdaExemption =
    canEdit || projectMemberRole === 'owner' || projectMemberRole === 'admin';

  if (!canEdit) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <Card className={WIDGET_ALERT_WARNING_CLASS}>
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-6 h-6 mt-1 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`} />
              <div>
                <h3 className={`mb-2 font-semibold ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>Access Restricted</h3>
                <p className={`mb-4 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                  Only project owners and admins can access project settings.
                </p>
                <Button onClick={() => navigate(`/app/projects/${projectId}`)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Project
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Project name is required');
      return;
    }

    const now = new Date().toISOString();
    const ndaAdditionalClauses = ndaAdditionalClausesText
      .split('\n')
      .map((c) => c.trim())
      .filter(Boolean);
    const currentNdaSettings = getEffectiveProjectNdaSettings(project);
    const exemptRoles = canManageNdaExemption
      ? ndaExemptStakeholders
        ? [...new Set([...(currentNdaSettings.exemptRoles || []).filter(r => !EXEMPTABLE_ROLES.includes(r as UserRole)), 'stakeholder' as UserRole])
          ]
        : (currentNdaSettings.exemptRoles || []).filter((r) => r !== 'stakeholder')
      : currentNdaSettings.exemptRoles || [];

    const updates = {
      name: name.trim(),
      description: description.trim(),
      projectLeads: selectedLeads,
      teamMembers: selectedMembers,
      ndaSettings: {
        ...currentNdaSettings,
        exemptRoles,
        additionalClauses: ndaAdditionalClauses,
        updatedAt: now,
        updatedBy: currentUser.id,
        updatedByName: currentUser.name,
      },
      accountingModules: {
        sr: accountingModules.sr,
        fcm: accountingModules.fcm,
      },
      updatedAt: now,
    };

    await updateProject(projectId!, updates);
    toast.success('Project settings updated');
    const email = state.currentUser?.email;
    if (email) await reload(email);
  };

  const handleAddLead = (userId: string) => {
    if (!selectedLeads.includes(userId)) {
      setSelectedLeads([...selectedLeads, userId]);
    }
  };

  const handleRemoveLead = (userId: string) => {
    setSelectedLeads(selectedLeads.filter(id => id !== userId));
  };

  const handleAddMember = (userId: string, role: UserRole) => {
    if (!selectedMembers.find(m => m.userId === userId)) {
      setSelectedMembers([...selectedMembers, { userId, role }]);
    }
  };

  const handleUpdateMemberRole = (userId: string, role: UserRole) => {
    setSelectedMembers(
      selectedMembers.map(m => m.userId === userId ? { ...m, role } : m)
    );
  };

  const handleRemoveMember = (userId: string) => {
    setSelectedMembers(selectedMembers.filter(m => m.userId !== userId));
  };

  const handleCreateUser = async () => {
    if (!createUserForm.name.trim()) {
      toast.error('User name is required');
      return;
    }
    if (!createUserForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!createUserForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!project) {
      toast.error('Project not found');
      return;
    }

    setIsCreatingUser(true);
    try {
      const normalizedEmail = createUserForm.email.trim().toLowerCase();
      const existingUser = users.find(
        (user) =>
          user.organizationId === project.organizationId &&
          user.email.trim().toLowerCase() === normalizedEmail
      );

      if (existingUser) {
        const alreadyMember = selectedMembers.some((member) => member.userId === existingUser.id);
        if (alreadyMember) {
          toast.info(`User "${existingUser.name}" is already in this project`);
        } else {
          handleAddMember(existingUser.id, createUserForm.role);
          toast.success(`Existing user "${existingUser.name}" added to project`);
        }
        setCreateUserForm({ name: '', email: '', role: 'developer' });
        return;
      }

      // Create the new user
      const newUser: User = {
        id: normalizedEmail,
        name: createUserForm.name.trim(),
        email: normalizedEmail,
        organizationId: project.organizationId,
        role: 'member',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await addUser(newUser);
      
      // Add the user to the project with the selected role
      handleAddMember(normalizedEmail, createUserForm.role);

      const userName = createUserForm.name.trim();
      // Reset the form
      setCreateUserForm({ name: '', email: '', role: 'developer' });
      toast.success(`User "${userName}" created and added to project`);
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Failed to create user');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleApplyCostTemplate = async (templateName: string) => {
    try {
      if (!project) {
        toast.error('Project not found');
        return;
      }

      if (!projectId) {
        toast.error('Project ID not found');
        return;
      }

      const template = costTemplates.find(t => t.name === templateName);
      if (!template) {
        toast.error('Template not found');
        return;
      }

      const updatedProject = applyCostTemplate(project, template);
      await updateProject(projectId, {
        ...updatedProject,
        updatedAt: new Date().toISOString(),
      });
      setAppliedTemplate(templateName);
      toast.success(`Cost template "${templateName}" applied successfully`);
      const email = state.currentUser?.email;
      if (email) await reload(email);
    } catch (error: any) {
      console.error('Error applying cost template:', error);
      toast.error(error.message || 'Failed to apply template');
    }
  };

  const appendDescriptionSnippet = (snippet: string) => {
    setDescription((prev) => (prev ? `${prev}\n${snippet}` : snippet));
  };

  const handleInsertDescriptionLink = () => {
    const rawUrl = window.prompt('Enter link URL');
    const url = String(rawUrl || '').trim();
    if (!url) return;

    const rawLabel = window.prompt('Enter link text (optional)');
    const label = String(rawLabel || '').trim() || 'Link';
    appendDescriptionSnippet(`[${label}](${url})`);
  };

  const handleDescriptionImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach((file, index) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = String(event.target?.result || '').trim();
        if (!imageUrl) return;

        const safeName = file.name?.trim() || `Project image ${index + 1}`;
        appendDescriptionSnippet(`![${safeName}](${imageUrl})`);
      };
      reader.readAsDataURL(file);
    });

    if (e.target) e.target.value = '';
  };

  const availableUsers = users.filter(u => u.organizationId === project.organizationId);
  const availableLeads = availableUsers.filter(u => !selectedLeads.includes(u.id));
  const availableMembers = availableUsers.filter(u => !selectedMembers.find(m => m.userId === u.id));

  const getUserById = (userId: string): User | undefined => {
    const normalizedMemberId = String(userId || '').trim().toLowerCase();
    return users.find((user) => {
      const normalizedUserId = String(user.id || '').trim().toLowerCase();
      const normalizedUserEmail = String(user.email || '').trim().toLowerCase();
      return normalizedUserId === normalizedMemberId || normalizedUserEmail === normalizedMemberId;
    });
  };

  const renderUserProfileMeta = (user: User) => {
    return (
      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
        <UserContactLinks user={user} className="mt-0" />
        <Link
          to={getUserProfilePath(user, state.currentUser)}
          className="inline-flex items-center gap-1 text-foreground hover:underline"
        >
          View profile
        </Link>
      </div>
    );
  };

  // ── NDA helpers ──────────────────────────────────────────────────────────────
  const ndaSettings = getEffectiveProjectNdaSettings(project);
  const standardNdaClauses = getStandardProjectNdaClauses(
    state.currentOrganization?.name || project.organizationId,
    project.name
  );

  const formatDateTime = (value?: string): string => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const handleClearNda = async (signatureId: string, userLabel: string) => {
    const nextSignatures = revokeProjectNdaSignature(
      project.ndaSignatures,
      signatureId,
      { id: currentUser.id, name: currentUser.name }
    );
    await setProjectNdaSignatures(project.organizationId, project.id, nextSignatures);
    await updateProject(projectId!, {
      updatedAt: new Date().toISOString(),
    });
    toast.success(`Cleared NDA for ${userLabel}`);
    const email = state.currentUser?.email;
    if (email) await reload(email);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/app/projects/${projectId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Project
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl mb-2 flex items-center gap-2">
              <SettingsIcon className="w-8 h-8" />
              Project Settings
            </h1>
            <p className="text-muted-foreground">{project.name}</p>
          </div>
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="w-4 h-4" />
            Save Changes
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the project..."
                rows={4}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleInsertDescriptionLink}
                  className="gap-1.5"
                >
                  <Link2 className="h-4 w-4" />
                  Add Link
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => descriptionImageInputRef.current?.click()}
                  className="gap-1.5"
                >
                  <ImagePlus className="h-4 w-4" />
                  Add Picture
                </Button>
                <input
                  ref={descriptionImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleDescriptionImageUpload}
                  aria-label="Upload project description images"
                />
              </div>
              {descriptionImageUrls.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {descriptionImageUrls.map((url, index) => (
                    <a
                      key={`${url}-${index}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                    >
                      <img
                        src={url}
                        alt={`Description image ${index + 1}`}
                        className="h-16 w-16 rounded-lg border border-border object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Project Leads */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              Project Leads
            </CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Project leads are highlighted coordinators. Owners and admins have full settings access.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Leads */}
            {selectedLeads.length > 0 ? (
              <div className="space-y-2">
                {selectedLeads.map(userId => {
                  const user = getUserById(userId);
                  if (!user) return null;
                  
                  return (
                    <div
                      key={userId}
                      className={`flex items-center justify-between rounded-lg border p-3 ${WIDGET_ALERT_WARNING_CLASS}`}
                    >
                      <div className="flex items-center gap-3">
                        <Crown className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-semibold">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          {renderUserProfileMeta(user)}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveLead(userId)}
                        disabled={selectedLeads.length === 1} // Can't remove last lead
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/40 py-6 text-center text-muted-foreground">
                <Crown className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
                <p className="text-sm">No project leads assigned</p>
              </div>
            )}

            {/* Add Lead */}
            {availableLeads.length > 0 && (
              <div>
                <Label className="text-sm mb-2 block">Add Project Lead</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableLeads.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleAddLead(user.id)}
                      className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-3 transition-colors hover:bg-accent"
                    >
                      <div className="text-left">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                        {renderUserProfileMeta(user)}
                      </div>
                      <UserPlus className="h-4 w-4 text-primary" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members
            </CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Assign users to this project with specific roles.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create New User Section */}
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Create New Organization User
                </h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Create a new user in the organization and automatically add them to this project.
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="new-user-name" className="text-xs mb-1 block">User Name *</Label>
                    <Input
                      id="new-user-name"
                      placeholder="e.g., John Doe"
                      value={createUserForm.name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                      disabled={isCreatingUser}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-user-email" className="text-xs mb-1 block">Email *</Label>
                    <Input
                      id="new-user-email"
                      type="email"
                      placeholder="e.g., john@example.com"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                      disabled={isCreatingUser}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-user-role" className="text-xs mb-1 block">Initial Role</Label>
                    <select
                      id="new-user-role"
                      title="Initial role for new user"
                      aria-label="Initial role for new user"
                      value={createUserForm.role}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value as UserRole })}
                      className={`${APP_NATIVE_FIELD_CLASS} h-9 text-sm`}
                      disabled={isCreatingUser}
                    >
                      <option value="developer">Developer</option>
                      <option value="stakeholder">Stakeholder</option>
                      <option value="investor">Investor</option>
                      <option value="external_partner">External Partner</option>
                      <option value="hr">HR</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                </div>
                <Button
                  onClick={handleCreateUser}
                  disabled={isCreatingUser || !createUserForm.name.trim() || !createUserForm.email.trim()}
                  className="w-full gap-2"
                  size="sm"
                >
                  <UserPlus className="w-4 h-4" />
                  {isCreatingUser ? 'Creating...' : 'Create & Add User'}
                </Button>
              </div>
            </div>

            {/* Current Members */}
            {selectedMembers.length > 0 ? (
              <div className="space-y-2">
                {selectedMembers.map(member => {
                  const user = getUserById(member.userId);
                  if (!user) return null;
                  
                  return (
                    <div
                      key={member.userId}
                      className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex-1">
                          <div className="font-semibold">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          {renderUserProfileMeta(user)}
                        </div>
                        <select
                          aria-label={`Role for ${user.name}`}
                          value={member.role}
                          onChange={(e) => handleUpdateMemberRole(member.userId, e.target.value as UserRole)}
                          className={`${APP_NATIVE_FIELD_CLASS} h-9 min-w-[10rem] text-sm`}
                        >
                          <option value="developer">Developer</option>
                          <option value="stakeholder">Stakeholder</option>
                          <option value="investor">Investor</option>
                          <option value="external_partner">External Partner</option>
                          <option value="hr">HR</option>
                          <option value="admin">Admin</option>
                          <option value="owner">Owner</option>
                        </select>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.userId)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/40 py-6 text-center text-muted-foreground">
                <Users className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
                <p className="text-sm">No team members assigned</p>
              </div>
            )}

            {/* Add Member */}
            {availableMembers.length > 0 && (
              <div>
                <Label className="text-sm mb-2 block">Add Team Member</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableMembers.map(user => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-card p-3"
                    >
                      <div className="text-left flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                        {renderUserProfileMeta(user)}
                      </div>
                      <select
                        aria-label={`Add ${user.name} with role`}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAddMember(user.id, e.target.value as UserRole);
                          }
                        }}
                        className={`${APP_NATIVE_FIELD_CLASS} mr-2 h-9 min-w-[10rem] text-sm`}
                        defaultValue=""
                      >
                        <option value="">Select role...</option>
                        <option value="developer">Developer</option>
                        <option value="stakeholder">Stakeholder</option>
                        <option value="investor">Investor</option>
                        <option value="external_partner">External Partner</option>
                        <option value="hr">HR</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* NDA Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              Project NDA
            </CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              By default every user must sign an NDA before accessing this project. Stakeholders
              (view-only, no edit access) can be exempted below. Only project owners and admins
              can change the exemption.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Stakeholder exemption toggle */}
            <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4">
              <div className="text-sm font-medium text-foreground">Stakeholder NDA exemption</div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="nda-exempt-stakeholder"
                  checked={ndaExemptStakeholders}
                  onCheckedChange={(v) => {
                    if (canManageNdaExemption) setNdaExemptStakeholders(v === true);
                  }}
                  disabled={!canManageNdaExemption}
                />
                <Label
                  htmlFor="nda-exempt-stakeholder"
                  className={`text-sm leading-5 ${canManageNdaExemption ? 'cursor-pointer text-foreground' : 'cursor-not-allowed text-muted-foreground'}`}
                >
                  Exempt users with the <strong>Stakeholder</strong> role from signing the NDA
                </Label>
              </div>
              {!canManageNdaExemption && (
                <p className={`rounded px-3 py-2 text-xs ${WIDGET_ALERT_WARNING_CLASS} ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                  Only project owners and admins can change this setting.
                </p>
              )}
              {ndaExemptStakeholders && (
                <p className="text-xs text-muted-foreground">
                  Stakeholders will bypass the NDA gate. All other roles still require a signature.
                </p>
              )}
            </div>

            {/* Standard clauses (read-only) */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 text-sm font-medium text-foreground">
                Standard clauses (always included)
              </div>
              <div className="space-y-2">
                {standardNdaClauses.map((clause, i) => (
                  <p key={i} className="text-sm leading-6 text-muted-foreground">
                    • {clause}
                  </p>
                ))}
              </div>
            </div>

            {/* Additional clauses */}
            <div>
              <Label htmlFor="nda-additional">Additional Project Clauses</Label>
              <Textarea
                id="nda-additional"
                value={ndaAdditionalClausesText}
                onChange={(e) => setNdaAdditionalClausesText(e.target.value)}
                placeholder="One additional clause per line…"
                rows={5}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Appended after the standard clauses in the generated PDF.
              </p>
            </div>

            {/* Signature tracking */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-1 text-sm font-medium text-foreground">Signature tracking</div>
              <p className="mb-4 text-sm text-muted-foreground">
                Revoking a signature forces that user to re-sign the next time they enter the project.
              </p>
              {ndaStatusRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No project users to track yet.</p>
              ) : (
                <div className="space-y-3">
                  {ndaStatusRows.map((row) => {
                    const isSigned = !!row.signature;
                    return (
                      <div
                        key={`${row.email}-${row.label}`}
                        className="flex flex-col gap-3 rounded-lg border border-border p-3 md:flex-row md:items-center md:justify-between"
                      >
                        <div>
                          <div className="font-medium text-foreground">{row.label}</div>
                          <div className="text-sm text-muted-foreground">{row.email}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {isSigned
                              ? `Signed ${formatDateTime(row.signature?.signedAt)}`
                              : 'Not yet signed'}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-medium ${
                              isSigned
                                ? WIDGET_STATUS_BADGE_SUCCESS_CLASS
                                : WIDGET_STATUS_BADGE_WARNING_CLASS
                            }`}
                          >
                            {isSigned ? 'Signed' : 'Pending'}
                          </span>
                          {row.signature?.pdfUrl && (
                            <a
                              href={row.signature.pdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              PDF
                            </a>
                          )}
                          {row.signature && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleClearNda(row.signature!.id, row.label)}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Accounting Modules
            </CardTitle>
            <p className="mt-2 text-sm text-muted-foreground">
              Enable optional accounting modules. Only enabled modules appear under project navigation.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
              <Checkbox
                id="module-sr"
                checked={accountingModules.sr}
                onCheckedChange={(checked) => setAccountingModules((prev) => ({ ...prev, sr: checked === true }))}
              />
              <div>
                <Label htmlFor="module-sr" className="cursor-pointer">Software Requirements (SR)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Tracks SR-tagged requirements, milestones, and ledger transactions.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
              <Checkbox
                id="module-fcm"
                checked={accountingModules.fcm}
                onCheckedChange={(checked) => setAccountingModules((prev) => ({ ...prev, fcm: checked === true }))}
              />
              <div>
                <Label htmlFor="module-fcm" className="cursor-pointer">
                  Financial & Construction Milestones (FCM)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Tracks FCM-tagged requirements, milestone-level revenue/cost/tax, and capital ledger.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cost Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Cost Templates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Apply pre-configured cost structures to your project. These templates define cost item categories and payment schedules.
            </p>
            <div className="space-y-3">
              {costTemplates.map((template) => (
                <div
                  key={template.name}
                  className="flex items-start justify-between rounded-lg border border-border p-3 transition hover:bg-accent"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{template.name}</h4>
                    <p className="mb-2 text-xs text-muted-foreground">{template.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {template.costItems.map((item, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${WIDGET_STATUS_BADGE_INFO_CLASS}`}
                        >
                          {item.description}
                          {item.code ? ` (${item.code})` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleApplyCostTemplate(template.name)}
                    disabled={appliedTemplate === template.name}
                    size="sm"
                    className="ml-4 flex-shrink-0"
                  >
                    {appliedTemplate === template.name ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Applied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Apply
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Permissions Summary */}
        <Card className={WIDGET_ALERT_INFO_CLASS}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Shield className="w-5 h-5" />
              Permissions Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <Crown className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <strong className="text-foreground">Project Owners</strong>
                <p className="text-muted-foreground">Can edit all project settings, manage members, and change project governance controls</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Shield className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <strong className="text-foreground">Admins</strong>
                <p className="text-muted-foreground">Have full access to all projects and can make all changes</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 text-primary" />
              <div>
                <strong className="text-foreground">Team Members</strong>
                <p className="text-muted-foreground">Can work on tasks based on their assigned role</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
